import React from 'react';
import { cn } from '../index';

/** Apply alpha to a hex color (#RGB, #RGBA, #RRGGBB, #RRGGBBAA) or CSS color. Falls back to muted if invalid. */
export function withAlpha(color: string | null | undefined, alpha = 0.15): string {
  if (!color) return 'var(--muted)';
  const hex = color.trim();
  const hex3 = /^#([0-9a-fA-F]{3})$/.exec(hex);
  const hex4 = /^#([0-9a-fA-F]{4})$/.exec(hex);
  const hex6 = /^#([0-9a-fA-F]{6})$/.exec(hex);
  const hex8 = /^#([0-9a-fA-F]{8})$/.exec(hex);

  if (hex3?.[1]) {
    const s = hex3[1];
    const r = parseInt((s[0] ?? '') + (s[0] ?? ''), 16);
    const g = parseInt((s[1] ?? '') + (s[1] ?? ''), 16);
    const b = parseInt((s[2] ?? '') + (s[2] ?? ''), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex4?.[1]) {
    const s = hex4[1];
    const r = parseInt((s[0] ?? '') + (s[0] ?? ''), 16);
    const g = parseInt((s[1] ?? '') + (s[1] ?? ''), 16);
    const b = parseInt((s[2] ?? '') + (s[2] ?? ''), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex6?.[1]) {
    const s = hex6[1];
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex8?.[1]) {
    const s = hex8[1];
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Fallback for CSS variables, hsl, rgb, or named colors using standard CSS color-mix
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

export interface ColorBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  color?: string | null;
  children?: React.ReactNode;
  className?: string;
}

export const ColorBadge: React.FC<ColorBadgeProps> = ({
  color,
  children,
  className,
  style,
  ...props
}) => {
  const badgeStyle: React.CSSProperties = {
    backgroundColor: withAlpha(color ?? '#4f46e5', 0.12),
    borderColor: withAlpha(color ?? '#4f46e5', 0.3),
    color: color ?? 'var(--fg)',
    ...(style ? style : {}),
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        className,
      )}
      style={badgeStyle}
      {...props}
    >
      {children}
    </span>
  );
};
